'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { apiClient } from '@/lib/api-client'
import { appendScopeFilterParams } from '@/lib/dashboard-scope-filters'

interface ParametersViewProps {
  startDate: string
  endDate: string
  isLoading?: boolean
  targetEmployeeId?: number
  selectedProduct?: string
  selectedSite?: string
}

interface PolicyViolation {
  policy_name: string
  policy_id: number | null
  violation_count: number
  is_fatal: boolean
  affected_calls: number
  violation_rate: number
}

interface PolicyViolationsData {
  total_violations: number
  total_calls_analyzed: number
  violations: PolicyViolation[]
}

interface ParameterData {
  name: string
  passRate: number
  totalCalls: number
  violationCount: number
  isFatal: boolean
}

function getBarColor(passRate: number): string {
  if (passRate >= 85) return '#10b981' // emerald-500
  if (passRate >= 70) return '#3b82f6' // blue-500
  if (passRate >= 55) return '#f59e0b' // amber-500
  return '#ef4444' // red-500
}

export function ParametersView({ startDate, endDate, isLoading = false, targetEmployeeId, selectedProduct, selectedSite }: ParametersViewProps) {
  const [parameters, setParameters] = useState<ParameterData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (startDate && endDate) {
      fetchPolicyData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, targetEmployeeId, selectedProduct, selectedSite])

  const fetchPolicyData = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
      if (targetEmployeeId) params.set('target_employee_id', String(targetEmployeeId))
      appendScopeFilterParams(params, selectedProduct, selectedSite)
      const data = await apiClient.get<PolicyViolationsData>(
        `/api/analysis/policy-violations?${params}`
      )
      
      // Convert policy violations to parameter data with pass rates
      const paramData: ParameterData[] = data.violations.map(violation => {
        // Calculate pass rate: (total_calls - affected_calls) / total_calls * 100
        const passRate = data.total_calls_analyzed > 0
          ? ((data.total_calls_analyzed - violation.affected_calls) / data.total_calls_analyzed) * 100
          : 100
        
        return {
          name: violation.policy_name,
          passRate: Math.round(passRate * 10) / 10, // Round to 1 decimal
          totalCalls: data.total_calls_analyzed,
          violationCount: violation.violation_count,
          isFatal: violation.is_fatal,
        }
      })
      
      // Sort by pass rate (ascending) to show worst performers first
      paramData.sort((a, b) => a.passRate - b.passRate)
      
      setParameters(paramData)
    } catch (err) {
      console.error('Failed to fetch policy data:', err)
      setError('Failed to load policy data')
    } finally {
      setLoading(false)
    }
  }

  if (loading || isLoading) {
    return (
      <div className='space-y-6'>
        <Skeleton className='h-12 w-full' />
        <Skeleton className='h-[400px] w-full' />
        <div className='grid gap-4 md:grid-cols-2'>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className='h-32' />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='rounded-xl border border-red-200 bg-red-50 p-6 text-center'>
        <p className='text-red-700'>{error}</p>
      </div>
    )
  }

  if (parameters.length === 0) {
    return (
      <div className='rounded-xl border border-gray-200 bg-gray-50 p-12 text-center'>
        <h3 className='text-lg font-semibold text-gray-700'>No policy data available</h3>
        <p className='mt-2 text-sm text-gray-500'>
          Analyze some calls first to see parameter pass rates
        </p>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h2 className='text-display-5 font-bold text-brand-navy'>Policy Compliance Analysis</h2>
        <p className='mt-1 text-sm text-muted-foreground'>
          Detailed breakdown of all policy pass rates
        </p>
      </motion.div>

      {/* Quick Stats Summary */}
      <div className='grid gap-4 md:grid-cols-3'>
        <Card>
          <CardContent className='p-4'>
            <p className='text-xs text-muted-foreground'>Total Policies</p>
            <p className='text-2xl font-bold text-brand-navy'>{parameters.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <p className='text-xs text-muted-foreground'>Average Pass Rate</p>
            <p className='text-2xl font-bold text-brand-cornflower'>
              {parameters.length > 0 
                ? (parameters.reduce((sum, p) => sum + p.passRate, 0) / parameters.length).toFixed(1)
                : 0}%
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <p className='text-xs text-muted-foreground'>Total Violations</p>
            <p className='text-2xl font-bold text-red-600'>
              {parameters.reduce((sum, p) => sum + p.violationCount, 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Policy Cards Grid */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h3 className='mb-3 text-lg font-semibold text-brand-navy'>All Policies</h3>
        <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
          {parameters.map((param, index) => (
            <Card key={param.name} className={cn(
              'relative overflow-hidden border-2',
              param.isFatal && 'border-red-300 bg-red-50/30'
            )}>
              <CardWatermark opacity={2} scale={0.8} />
              <CardContent className='relative z-10 p-5'>
                <div className='mb-3 flex items-start justify-between'>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <h4 className='font-semibold text-brand-navy truncate'>{param.name}</h4>
                      {param.isFatal && (
                        <span className='shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700'>
                          Fatal
                        </span>
                      )}
                    </div>
                    <p className='text-xs text-muted-foreground mt-1'>
                      {param.totalCalls} calls analyzed
                    </p>
                  </div>
                  <div
                    className={cn(
                      'shrink-0 rounded-lg px-2 py-1 text-xs font-bold',
                      param.passRate >= 85 && 'bg-emerald-100 text-emerald-700',
                      param.passRate >= 70 && param.passRate < 85 && 'bg-blue-100 text-blue-700',
                      param.passRate >= 55 && param.passRate < 70 && 'bg-amber-100 text-amber-700',
                      param.passRate < 55 && 'bg-red-100 text-red-700'
                    )}
                  >
                    {param.passRate >= 85 ? 'Excellent' : param.passRate >= 70 ? 'Good' : param.passRate >= 55 ? 'Fair' : 'Poor'}
                  </div>
                </div>

                <div className='space-y-3'>
                  {/* Pass Rate */}
                  <div>
                    <div className='mb-1 flex items-baseline justify-between'>
                      <span className='text-xs text-muted-foreground'>Pass Rate</span>
                      <span className='text-lg font-bold text-brand-navy'>{param.passRate.toFixed(1)}%</span>
                    </div>
                    <div className='h-2 overflow-hidden rounded-full bg-gray-100'>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${param.passRate}%` }}
                        transition={{ duration: 0.8, delay: index * 0.05 }}
                        className='h-full rounded-full'
                        style={{ backgroundColor: getBarColor(param.passRate) }}
                      />
                    </div>
                  </div>

                  {/* Violations */}
                  <div>
                    <div className='mb-1 flex items-baseline justify-between'>
                      <span className='text-xs text-muted-foreground'>Violations</span>
                      <span className={cn(
                        'text-lg font-bold',
                        param.violationCount > 0 ? 'text-red-600' : 'text-emerald-600'
                      )}>
                        {param.violationCount}
                      </span>
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      {param.violationCount === 0 
                        ? 'Perfect compliance' 
                        : `${param.violationCount} violation${param.violationCount !== 1 ? 's' : ''} found`
                      }
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.div>
    </div>
  )
}

